import { useMemo, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { Command } from 'cmdk'
import { Plus, Search, Filter } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { FilterChip } from './FilterChip'
import { useDebounce } from '@/hooks/useDebounce'
import { formatChannel } from '@/lib/format'
import { ScenarioMultiSelect } from '@/features/scenarios/components/ScenarioMultiSelect'
import { formatScenarioSelection } from '@/features/scenarios/lib/format'
import { useQueueDepths } from '../../hooks/useQueueDepths'
import type {
  AlertFilters,
  AlertStatus,
  Priority,
} from '../../types'
import type { Channel } from '@/features/transactions/types'

const STATUSES: AlertStatus[] = [
  'open',
  'in_review',
  'escalated',
  'merged',
  'closed_fraud',
  'closed_false_positive',
  'closed_duplicate',
  'closed_other',
]
const PRIORITIES: Priority[] = ['critical', 'high', 'medium', 'low']
const CHANNELS: Channel[] = [
  'pos',
  'nip',
  'rtgs',
  'ussd',
  'mobile_app',
  'internet_banking',
  'agent_banking',
  'atm',
]

interface Props {
  filters: AlertFilters
  onChange: (next: AlertFilters) => void
  onClearAll: () => void
  /**
   * Removes the `queue_id` filter AND the `queue` URL param it was
   * seeded from (see `AlertsQueuePage`). Distinct from the generic
   * `onChange`-based clears every other chip uses because `queue` is
   * the one dimension mirrored into the URL — clearing filter state
   * alone would leave a stale `?queue=` for a refresh to silently
   * reapply.
   */
  onClearQueue: () => void
}

type Dimension =
  | 'status'
  | 'priority'
  | 'scenario'
  | 'channel'
  | 'sla_breached'
  | 'amount_range'
  | 'q'
  | 'queue'

const DIMENSION_LABEL: Record<Dimension, string> = {
  status: 'Status',
  priority: 'Priority',
  scenario: 'Scenario',
  channel: 'Channel',
  sla_breached: 'SLA',
  amount_range: 'Amount',
  q: 'Search',
  queue: 'Queue',
}

// Not offered in the "Add filter" picker (`ADD_FILTER_DIMENSIONS` below) —
// this chip only ever appears when a `?queue=` deep link seeded it, as a
// visibility + clear affordance. A manual queue picker is a bigger design
// surface (search/select UI over the live queue catalogue) that's out of
// scope here.
const ADD_FILTER_DIMENSIONS = [
  'status',
  'priority',
  'scenario',
  'channel',
  'sla_breached',
  'amount_range',
] as const

export function FilterBar({ filters, onChange, onClearAll, onClearQueue }: Props) {
  // Debounce free-text search so each keystroke doesn't refetch.
  const [searchInput, setSearchInput] = useState(filters.q ?? '')
  const debouncedSearch = useDebounce(searchInput, 300)

  // Sync debounced search back into the filter set.
  if (debouncedSearch !== (filters.q ?? '')) {
    onChange({ ...filters, q: debouncedSearch || undefined })
  }

  // Resolves `queue_id` UUIDs to display names for the chip below. Reuses
  // the sidebar's live query (react-query dedupes the request) rather than
  // fetching a fresh queue catalogue just for this label.
  const { data: queueDepthsData } = useQueueDepths()
  const queueNameById = useMemo(() => {
    const map: Record<string, string> = {}
    for (const q of queueDepthsData?.queues ?? []) map[q.queue_id] = q.queue_name
    return map
  }, [queueDepthsData])

  const activeChips: Dimension[] = []
  if (filters.queue_id?.length) activeChips.push('queue')
  if (filters.status?.length) activeChips.push('status')
  if (filters.priority?.length) activeChips.push('priority')
  // Scenario chip stays mounted whenever the dimension is engaged,
  // even with zero codes picked, so users land in the picker after
  // selecting "Scenario" from the Add-filter popover. Clearing the
  // chip resets the field to undefined, which removes it.
  if (filters.scenario !== undefined) activeChips.push('scenario')
  if (filters.channel?.length) activeChips.push('channel')
  if (filters.sla_breached) activeChips.push('sla_breached')
  if (filters.amount_min_minor || filters.amount_max_minor) activeChips.push('amount_range')

  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      <div className="relative">
        <Search
          size={12}
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-fg"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search alerts…"
          aria-label="Search alerts"
          className="h-8 w-56 pl-7 text-sm"
        />
      </div>

      {activeChips.includes('queue') && (
        <FilterChip
          label="Queue"
          value={filters.queue_id!.map((id) => queueNameById[id] ?? id).join(', ')}
          active
          onClear={onClearQueue}
        >
          <p className="text-xs text-muted-fg">
            Filtered to{' '}
            <span className="font-medium text-fg">
              {filters.queue_id!.map((id) => queueNameById[id] ?? id).join(', ')}
            </span>{' '}
            via a queue link. Clear to see every queue.
          </p>
        </FilterChip>
      )}

      {activeChips.includes('status') && (
        <FilterChip
          label="Status"
          value={filters.status!.join(', ')}
          active
          onClear={() => onChange({ ...filters, status: undefined })}
        >
          <CheckboxGroup
            label="Status"
            options={STATUSES}
            selected={filters.status ?? []}
            onChange={(next) => onChange({ ...filters, status: next })}
          />
        </FilterChip>
      )}

      {activeChips.includes('priority') && (
        <FilterChip
          label="Priority"
          value={filters.priority!.join(', ')}
          active
          onClear={() => onChange({ ...filters, priority: undefined })}
        >
          <CheckboxGroup
            label="Priority"
            options={PRIORITIES}
            selected={filters.priority ?? []}
            onChange={(next) => onChange({ ...filters, priority: next })}
          />
        </FilterChip>
      )}

      {activeChips.includes('scenario') && (
        <FilterChip
          label="Scenario"
          value={formatScenarioSelection(filters.scenario ?? []) || 'Select…'}
          active
          onClear={() => onChange({ ...filters, scenario: undefined })}
          contentClassName="w-96 p-1"
        >
          <ScenarioMultiSelect
            selected={filters.scenario ?? []}
            onChange={(next) => onChange({ ...filters, scenario: next })}
          />
        </FilterChip>
      )}

      {activeChips.includes('channel') && (
        <FilterChip
          label="Channel"
          value={filters.channel!.map(formatChannel).join(', ')}
          active
          onClear={() => onChange({ ...filters, channel: undefined })}
        >
          <CheckboxGroup
            label="Channel"
            options={CHANNELS}
            renderLabel={formatChannel}
            selected={filters.channel ?? []}
            onChange={(next) => onChange({ ...filters, channel: next })}
          />
        </FilterChip>
      )}

      {activeChips.includes('sla_breached') && (
        <FilterChip
          label="SLA"
          value="At-risk"
          active
          onClear={() => onChange({ ...filters, sla_breached: undefined })}
        >
          <p className="text-xs text-muted-fg">
            Showing alerts whose SLA has been breached.
          </p>
        </FilterChip>
      )}

      {activeChips.includes('amount_range') && (
        <FilterChip
          label="Amount"
          value={amountRangeLabel(filters)}
          active
          onClear={() =>
            onChange({
              ...filters,
              amount_min_minor: undefined,
              amount_max_minor: undefined,
            })
          }
        >
          <AmountRangeForm filters={filters} onChange={onChange} />
        </FilterChip>
      )}

      <AddFilterPopover filters={filters} onChange={onChange} />

      {activeChips.length > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          className="ml-auto text-xs text-muted-fg hover:text-fg hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  )
}

function CheckboxGroup<T extends string>({
  label,
  options,
  selected,
  onChange,
  renderLabel,
}: {
  label: string
  options: readonly T[]
  selected: readonly T[]
  onChange: (next: T[]) => void
  renderLabel?: (v: T) => string
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-fg">{label}</p>
      {options.map((opt) => {
        const checked = selected.includes(opt)
        return (
          <label
            key={opt}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => {
                const next: T[] = e.target.checked
                  ? [...selected, opt]
                  : selected.filter((v) => v !== opt)
                onChange(next)
              }}
              className="h-3.5 w-3.5 accent-primary"
            />
            <span className="capitalize">{renderLabel ? renderLabel(opt) : opt.replace('_', ' ')}</span>
          </label>
        )
      })}
    </div>
  )
}

function AmountRangeForm({
  filters,
  onChange,
}: {
  filters: AlertFilters
  onChange: (next: AlertFilters) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-fg">
        Amount (₦)
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          inputMode="numeric"
          placeholder="Min"
          value={
            filters.amount_min_minor !== undefined
              ? filters.amount_min_minor / 100
              : ''
          }
          onChange={(e) =>
            onChange({
              ...filters,
              amount_min_minor: e.target.value
                ? Math.round(Number(e.target.value) * 100)
                : undefined,
            })
          }
          className="h-7 text-sm"
          aria-label="Minimum amount in Naira"
        />
        <span className="text-xs text-muted-fg">to</span>
        <Input
          type="number"
          min={0}
          inputMode="numeric"
          placeholder="Max"
          value={
            filters.amount_max_minor !== undefined
              ? filters.amount_max_minor / 100
              : ''
          }
          onChange={(e) =>
            onChange({
              ...filters,
              amount_max_minor: e.target.value
                ? Math.round(Number(e.target.value) * 100)
                : undefined,
            })
          }
          className="h-7 text-sm"
          aria-label="Maximum amount in Naira"
        />
      </div>
    </div>
  )
}

function amountRangeLabel(filters: AlertFilters): string {
  const min = filters.amount_min_minor !== undefined ? filters.amount_min_minor / 100 : null
  const max = filters.amount_max_minor !== undefined ? filters.amount_max_minor / 100 : null
  if (min !== null && max !== null) return `₦${min.toLocaleString()}–${max.toLocaleString()}`
  if (min !== null) return `≥ ₦${min.toLocaleString()}`
  if (max !== null) return `≤ ₦${max.toLocaleString()}`
  return ''
}

function AddFilterPopover({
  filters,
  onChange,
}: {
  filters: AlertFilters
  onChange: (next: AlertFilters) => void
}) {
  const [open, setOpen] = useState(false)

  const apply = (key: Dimension): void => {
    setOpen(false)
    if (key === 'status') onChange({ ...filters, status: ['open'] })
    if (key === 'priority') onChange({ ...filters, priority: ['critical', 'high'] })
    // Open the scenario chip with an empty selection — the user picks
    // codes from the live catalogue, so seeding an arbitrary default
    // would land on a scenario the analyst didn't intend.
    if (key === 'scenario') onChange({ ...filters, scenario: [] })
    if (key === 'channel') onChange({ ...filters, channel: ['nip'] })
    if (key === 'sla_breached') onChange({ ...filters, sla_breached: true })
    if (key === 'amount_range')
      onChange({ ...filters, amount_min_minor: 100_000_00 })
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className="flex items-center gap-1 rounded border border-dashed border-border px-2 py-1 text-xs text-muted-fg hover:border-solid hover:bg-muted hover:text-fg">
        <Plus size={12} />
        Add filter
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="z-50 w-56 rounded-md border border-border bg-popover p-1"
        >
          <Command label="Add filter">
            <Command.Input
              placeholder="Filter by…"
              className="h-8 w-full bg-transparent px-2 text-sm outline-none placeholder:text-muted-fg"
            />
            <Command.List className="max-h-60 overflow-y-auto">
              <Command.Empty className="px-2 py-2 text-xs text-muted-fg">
                Nothing matches.
              </Command.Empty>
              {ADD_FILTER_DIMENSIONS.map(
                (k) => (
                  <Command.Item
                    key={k}
                    value={DIMENSION_LABEL[k]}
                    onSelect={() => apply(k)}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm aria-selected:bg-muted data-[selected=true]:bg-muted"
                  >
                    <Filter size={12} className="text-muted-fg" />
                    {DIMENSION_LABEL[k]}
                  </Command.Item>
                ),
              )}
            </Command.List>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
